import { TokenProvider } from '~/hooks/auth/useAccessTokenProvider';
import { handleError } from '~/utils/errors';
import {
    defaultRetryPolicy,
    FetchRequest,
    fetchAndReadWithRetry,
    bufferResponse,
    BlobResult,
    BodyReader,
    readBlob,
    HttpStatusError,
    isTransientNetworkError,
    RetryAttemptInfo,
    RetryPolicy,
} from './network-retry';
import { isAbortError } from './retryOn202';
import {
    APIType,
    ContouringCustomizationFile,
    DoseCustomizationFile,
    EmptyLabeling,
    Labeling,
    PollData,
    PollingStatus,
    RegistrationCustomizationFile,
    TranslationCustomizationFile,
    UserCustomization,
    UserModels,
} from './types';

import AppConfig from '~/utils/appconfig';

/**
 * Parameters for the request to upload and process DICOM files. This objects is passed as a header to the backend
 * for each request and needs to be filled according to the API specification.
 */
export type RequestParameters = {
    action: string;
    nExpectedFiles?: string;
    taskUID?: string;
    targets?: string; // for dose prediction
    predict_objectives?: string; // for dose prediction
    rois?: string; // for segmentation
    sessionId?: string; // if needs to be overriden
    fixedSeriesUID?: string;
    movingSeriesUID?: string;
};

export type BlobUploadType = { file: Blob; filename?: string };

// Define a type for the JSON returned from access-rights as a dictionary
interface AccessRightsType {
    [key: string]: string[];
}

/**
 * Response timeout for small status/metadata requests (poll, model
 * lists, labeling, …), tighter than the transport default: these
 * answer in well under a second, and a request stuck on a socket that
 * died during a network change should be retried soon rather than hold
 * a screen. Every other request still gets the transport's default
 * response timeout, which allows for its upload, and an inactivity
 * timeout on its download.
 */
export const STATUS_REQUEST_TIMEOUT_MS = 30_000;

export type BackendRequestOptions = {
    /** HTTP method; defaults to GET. */
    method?: string;
    /** Extra headers merged over the default (auth) headers. */
    headers?: Record<string, string>;
    /**
     * Builds the request body. Invoked once per attempt so a retry
     * never resends a consumed stream.
     */
    buildBody?: () => BodyInit;
    /** Extra query parameters (`sessionId` is always appended). */
    params?: Record<string, string>;
    /**
     * Retry behavior; {@link defaultRetryPolicy} when omitted. A call site
     * passing 'transient' where the default is 'none' documents why a
     * duplicate delivery is harmless to it.
     */
    retryPolicy?: RetryPolicy;
    /**
     * Overrides the transport's response timeout (before its upload
     * allowance); expiry counts as a transient failure.
     */
    responseTimeoutMs?: number;
    cache?: RequestCache;
    /**
     * Static description of the request for the retry log, for paths
     * that embed ids (e.g. `v2/images/{datasetId}`). Defaults to the
     * path.
     */
    label?: string;
    /** Invoked once per scheduled retry, after the retry is logged. */
    onRetry?: (info: RetryAttemptInfo) => void;
};

/** The backend's answer to a sign-in that is not linked to an MVision user. */
const ACCOUNT_NOT_LINKED = 'This account is not linked to an MVision user.';

/**
 * The backend refused the caller rather than failing to answer: the request was
 * understood, and the account it carried is not let in. Asking again with the
 * same credentials gets the same answer, so callers must not retry it.
 */
export class AccessRefusedError extends Error {
    constructor(readonly status: number) {
        super(`The backend refused this account: ${status}`);
        this.name = 'AccessRefusedError';
    }
}

/**
 * The backend could not answer at all: the request never reached it (DNS,
 * network, CORS) or it failed with a 5xx. Says nothing about the account.
 */
export class BackendUnavailableError extends Error {
    constructor(
        readonly status?: number,
        readonly url?: string
    ) {
        super(
            (status
                ? `Backend unavailable: ${status}`
                : 'Backend unavailable: no response') +
                (url ? ` from ${url}` : '')
        );
        this.name = 'BackendUnavailableError';
    }
}

export class BackendClient {
    public readonly sessionID: string;
    public readonly api: APIType;
    private baseURL: string;
    private tokenProvider: TokenProvider;
    protected abortController: AbortController = new AbortController();
    private readonly _accessRightKey: string = AppConfig.accessRightKey;

    constructor(
        baseURL: string,
        tokenProvider: TokenProvider,
        sessionID: string,
        api: APIType = APIType.CONTOURING
    ) {
        this.sessionID = sessionID;
        this.baseURL = baseURL.endsWith('/') ? baseURL : `${baseURL}/`;
        this.tokenProvider = tokenProvider;
        this.api = api;
    }

    authorizationHeader = async () => {
        const token = await this.tokenProvider.getToken();
        // don't add empty token
        return token?.token
            ? {
                  Authorization: token.token,
              }
            : Promise.resolve({});
    };

    defaultHeaders = async () => {
        return {
            ...(await this.authorizationHeader()),
            'Cache-Control': 'no-store',
        };
    };

    /**
     * Sends one backend request through the shared transient-retry
     * transport (`network-retry.ts`).
     *
     * Headers are resolved and the body rebuilt once per attempt, so a
     * retry carries a fresh bearer token — a long upload can outlive
     * the token that started it. Every request is bound to this
     * client's abort controller; cancellation stops both the attempt
     * in flight and any backoff wait.
     */
    protected request(
        path: string,
        options: BackendRequestOptions = {}
    ): Promise<Response> {
        return this.requestAndRead(path, options, bufferResponse);
    }

    /**
     * As {@link request}, with a 200 body read as one `Blob` inside the
     * attempt, so a large download is held once.
     */
    protected requestBlob(
        path: string,
        options: BackendRequestOptions = {}
    ): Promise<BlobResult> {
        return this.requestAndRead(path, options, readBlob);
    }

    private async requestAndRead<T>(
        path: string,
        options: BackendRequestOptions,
        readBody: BodyReader<T>
    ): Promise<T> {
        const url = this.envelopeURL(path, options.params);
        const method = options.method ?? 'GET';
        // Captured once: `abort()` swaps the controller, and a retry must
        // stay bound to the cancellation of the request it belongs to.
        const signal = this.abortController.signal;

        const buildRequest = async (): Promise<FetchRequest> => ({
            url,
            init: {
                method,
                headers: {
                    ...(await this.defaultHeaders()),
                    ...options.headers,
                },
                body: options.buildBody?.(),
                signal,
                cache: options.cache,
            },
        });

        return fetchAndReadWithRetry(
            buildRequest,
            {
                signal,
                // Path and method only — never the full URL (its query carries
                // the session id) and never header values.
                label: `${method} ${options.label ?? path}`,
                retryPolicy:
                    options.retryPolicy ??
                    defaultRetryPolicy(method, options.headers),
                responseTimeoutMs: options.responseTimeoutMs,
                onRetry: options.onRetry,
            },
            readBody
        );
    }

    public async models(): Promise<UserModels> {
        const response = await this.request(`${this.api}/list-active-models`, {
            responseTimeoutMs: STATUS_REQUEST_TIMEOUT_MS,
        });

        if (response.ok) {
            return response.json();
        } else {
            return {
                all_model_names: [],
                user_model_names: [],
                online_model_names: [],
                all_model_details: [],
                user_model_details: [],
            };
        }
    }

    public async accessRights(): Promise<boolean> {
        try {
            // One attempt; the caller owns retrying, with the long delays
            // a backend cold start needs. A transport retry underneath
            // would multiply each of its attempts by the whole backoff
            // window. The per-attempt timeout stays: a hung socket must
            // fail so the caller's retry fires.
            const response = await this.request('access-rights', {
                retryPolicy: 'none',
                responseTimeoutMs: STATUS_REQUEST_TIMEOUT_MS,
            }).catch((error: unknown) => {
                // No response came back (network failure or timeout).
                throw isTransientNetworkError(error)
                    ? new BackendUnavailableError(undefined, this.baseURL)
                    : error;
            });
            if (response.status >= 500) {
                throw new BackendUnavailableError(
                    response.status,
                    this.baseURL
                );
            }
            // Only the backend's own "not linked" 403 is an answer about the
            // account. Other 403s (a blocklist, a proxy) and a 401, which means
            // the request carried no usable sign-in, say nothing about the
            // account itself and may pass on asking again.
            if (response.status === 403) {
                const body = await response.json().catch(() => undefined);
                if (body?.detail === ACCOUNT_NOT_LINKED) {
                    throw new AccessRefusedError(response.status);
                }
            }
            if (!response.ok) {
                // Thrown so React Query retries it; the status tells a
                // refusal from a temporary server condition.
                throw new HttpStatusError(response.status, 'Access check');
            }
            const accessRights: AccessRightsType = await response.json();
            // Check if the specified key exists and includes 'access'
            if (
                accessRights[this._accessRightKey] &&
                accessRights[this._accessRightKey].includes('access')
            ) {
                return true;
            }
        } catch (error) {
            console.error(
                'An error occurred while fetching access rights:',
                error
            );
            throw error;
        }
        return false;
    }

    public async labeling(apiType: APIType): Promise<Labeling> {
        const urlPath =
            apiType === APIType.WORKSPACE ? 'labeling' : `${apiType}/labeling`;
        try {
            const response = await this.request(urlPath, {
                responseTimeoutMs: STATUS_REQUEST_TIMEOUT_MS,
            });
            if (!response.ok) {
                return EmptyLabeling;
            }
            return response.json();
        } catch (error) {
            console.error('An error occurred while fetching labeling:', error);
        }
        return EmptyLabeling;
    }

    /**
     * The identity the backend resolves this caller to.
     *
     * Only the backend can answer this: a broker subject is mapped to the
     * internal user id server-side, and that mapping is not present in the
     * token. Ownership checks compare against the resolved id, so anything
     * derived from token claims disagrees with the backend for any user whose
     * subject is not already the internal id.
     */
    public async identity(): Promise<{ user_id: string; user_name: string }> {
        const response = await this.request('user/me', {
            responseTimeoutMs: STATUS_REQUEST_TIMEOUT_MS,
        });
        if (!response.ok) {
            throw new HttpStatusError(response.status, 'Identity lookup');
        }
        return response.json();
    }

    /**
     * @param userId The backend user whose customizations to list; omitted
     *     where the connector injects its own identity upstream.
     */
    public async modelCustomizations(
        userId?: string
    ): Promise<
        UserCustomization<
            | TranslationCustomizationFile
            | DoseCustomizationFile
            | RegistrationCustomizationFile
            | ContouringCustomizationFile
        >[]
    > {
        const response = await this.request(
            `${APIType.CONFIG}/${this.customizationPath()}`,
            {
                // An absent id must not go out as the string "undefined".
                headers: userId === undefined ? {} : { userId },
                responseTimeoutMs: STATUS_REQUEST_TIMEOUT_MS,
            }
        );

        if (response.ok) {
            return response.json();
        } else {
            return [];
        }
    }

    /**
     * Uploads one file of a job. Returns whether the backend accepted it;
     * a rejection is reported to the user here.
     *
     * One attempt: the upload carries no key the backend could recognise
     * a re-send by, so a re-send whose first delivery landed may count as
     * one more file, and a task that declares its file count would start
     * on a scan with a slice missing.
     */
    public async submitFile(
        blob: BlobUploadType,
        request: RequestParameters
    ): Promise<boolean> {
        const params: { [index: string]: string } = {};

        if ('sessionId' in request && request.sessionId) {
            params['sessionId'] = request.sessionId!;
        }

        const response = await this.request(`${this.api}`, {
            method: 'POST',
            retryPolicy: 'none',
            headers: {
                ...request,
                skip_dicom_validation: 'False',
            },
            buildBody: () => {
                const data = new FormData();
                data.append('file', blob.file, blob.filename);
                return data;
            },
            params,
        });
        if (response.ok) {
            return true;
        } else {
            const userMessage = await this.extractErrorMessage(response);
            handleError(new Error(userMessage), userMessage);
        }

        return false;
    }

    public async poll(): Promise<PollData> {
        // One attempt; the caller owns retrying, its polling interval
        // being the retry spacing (see `createOutageTolerance`). The
        // per-attempt deadline stays, so a hung poll fails and the loop
        // moves on.
        const response = await this.request(`${this.api}/poll`, {
            method: 'POST',
            retryPolicy: 'none',
            responseTimeoutMs: STATUS_REQUEST_TIMEOUT_MS,
        });
        if (response.status === 304) {
            return { status: PollingStatus.NO_RESULT };
        } else if (response.status === 200) {
            const result = await response.json();
            return { status: PollingStatus.HAS_RESULT, result };
        }

        const error = await response.text();
        return {
            status: PollingStatus.POLLING_FAILED,
            error,
            httpStatus: response.status,
        };
    }

    public async downloadFile(
        resultId: string,
        path: string
    ): Promise<Blob | undefined> {
        try {
            const { response, blob } = await this.requestBlob(
                `${this.api}/result`,
                {
                    headers: { path },
                    params: { resultId },
                    cache: 'no-store',
                }
            );

            if (blob !== undefined) {
                return blob;
            } else {
                const userMessage = await this.extractErrorMessage(response);
                handleError(new Error(userMessage), userMessage);
            }
        } catch (error) {
            this.reportRequestFailure(
                error,
                "Couldn't download files succesfully."
            );
        }
    }

    public async deleteResult(resultId: string): Promise<string | undefined> {
        // Safe to retry although DELETE: the response status is not
        // inspected, so a duplicate delete reporting 404 changes
        // nothing for the caller.
        const response = await this.request(`${this.api}/result`, {
            method: 'DELETE',
            retryPolicy: 'transient',
            params: { resultId },
            cache: 'no-store',
            responseTimeoutMs: STATUS_REQUEST_TIMEOUT_MS,
        });
        return response.text();
    }

    /**
     * Reports a request that failed by throwing, unless it was cancelled
     * through {@link abort}: a cancellation is the user's own action, not
     * an error to show them or to send to Sentry.
     */
    protected reportRequestFailure(error: unknown, message: string): void {
        if (!isAbortError(error)) {
            handleError(error, message);
        }
    }

    public abort() {
        this.abortController.abort();
        this.abortController = new AbortController();
    }

    public get signal(): AbortSignal {
        return this.abortController.signal;
    }

    public async getErrors(resultId: string): Promise<string | undefined> {
        const response = await this.request(`${this.api}/error-message`, {
            params: { resultId },
            responseTimeoutMs: STATUS_REQUEST_TIMEOUT_MS,
        });

        if (response.ok) {
            return response.text();
        } else {
            return undefined;
        }
    }

    private async extractErrorMessage(response: Response): Promise<string> {
        const status = response.status;
        try {
            const text = await response.text();
            if (text && text.length < 300) {
                return `Server error (${status}): ${text}`;
            }
        } catch {
            // ignore parse failures
        }
        return `Server returned an unsuccessful response (status ${status}).`;
    }

    protected envelopeURL(
        path: string,
        params: { [index: string]: string } = {}
    ): string {
        const url = `${this.baseURL}${path}`;

        const queryParams = { ...params };
        if (!('sessionId' in queryParams)) {
            queryParams['sessionId'] = this.sessionID;
        }

        return `${url}?${new URLSearchParams(queryParams)}`;
    }

    private customizationPath(): string {
        switch (this.api) {
            case APIType.CONTOURING:
                return 'contouring';
            case APIType.DOSE:
                return 'dose';
            case APIType.DATA:
                return 'data';
            case APIType.REGISTRATION:
                return 'registration';
            case APIType.TRANSLATION:
                return 'translation';
            default:
                return 'contouring';
        }
    }
}
